import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Modal,
  Progress,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowRightOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ReloadOutlined,
  RetweetOutlined,
  SearchOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Layout from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { TestAttempt } from '../types';
import { formatTime, formatQuestionTime } from '../utils/formatTime';
import { getApiErrorMessage } from '../utils/apiError';
import './Results.css';

const { Title, Text, Paragraph } = Typography;

type AttemptFilter = 'all' | 'passed' | 'failed';

const Results: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState<TestAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState<number | null>(null);
  const [detailLoadingId, setDetailLoadingId] = useState<number | null>(null);
  const [detailedResults, setDetailedResults] = useState<Record<number, any>>({});
  const [attemptFilter, setAttemptFilter] = useState<AttemptFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFetchedStatsRef = useRef(false);

  const fetchAttempts = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const response = await api.get('/results/my-attempts');
      setAttempts(response.data ?? []);
    } catch (error: any) {
      console.error('Ошибка загрузки результатов:', error);
      const msg = error.response?.status === 401
        ? 'Требуется повторный вход'
        : getApiErrorMessage(error, 'Не удалось загрузить результаты');
      setLoadError(msg);
      setAttempts([]);
      if (error.response?.status !== 401) message.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') console.log('[RESULTS] fetch statistics');
      const response = await api.get('/results/my-statistics');
      setStats(response.data);
    } catch (error: any) {
      console.error('Ошибка загрузки статистики:', error);
      setStats(null);
    }
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') console.log('[RESULTS_PAGE] mount');
    fetchAttempts();
    if (!hasFetchedStatsRef.current) {
      hasFetchedStatsRef.current = true;
      fetchStats();
    }
    return () => {
      if (process.env.NODE_ENV === 'development') console.log('[RESULTS_PAGE] unmount');
    };
  }, [fetchAttempts, fetchStats]);

  const openAttemptDetails = useCallback(async (attemptId: number) => {
    setSelectedAttemptId(attemptId);
    if (detailedResults[attemptId]) return;

    setDetailLoadingId(attemptId);
    try {
      const response = await api.get(`/results/my-attempts/${attemptId}/details`);
      setDetailedResults(prev => ({ ...prev, [attemptId]: response.data }));
    } catch (error) {
      console.error('Ошибка загрузки деталей:', error);
      message.error('Не удалось загрузить детализацию попытки');
    } finally {
      setDetailLoadingId(null);
    }
  }, [detailedResults]);

  const downloadReport = useCallback((attemptId: number) => {
    window.open(`/api/v1/results/my-attempts/${attemptId}/export-pdf`, '_blank');
  }, []);

  // Number the participant's complete history before applying search/status filters.
  const attemptNumbers = useMemo(() => new Map(attempts.slice().sort((a, b) => (
    dayjs(a.started_at).valueOf() - dayjs(b.started_at).valueOf() || a.id - b.id
  )).map((attempt, index) => [attempt.id, index + 1])), [attempts]);

  const filteredAttempts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase('ru');
    return attempts.filter((attempt) => {
      if (attemptFilter === 'passed' && !attempt.is_passed) return false;
      if (attemptFilter === 'failed' && attempt.is_passed) return false;
      if (!normalizedQuery) return true;
      return (attempt.test_title || 'Тест').toLocaleLowerCase('ru').includes(normalizedQuery);
    });
  }, [attemptFilter, attempts, searchQuery]);

  const selectedAttempt = selectedAttemptId === null
    ? null
    : attempts.find(attempt => attempt.id === selectedAttemptId) || null;
  const selectedDetails = selectedAttemptId === null ? null : detailedResults[selectedAttemptId];

  const getBlockName = (blockName: string) => {
    if (blockName === 'products') return 'Ассортимент и запчасти';
    if (blockName === 'vehicles') return 'Автомобили и модели';
    return blockName;
  };

  if (loading) {
    return (
      <Layout>
        <div className="results-loading"><Spin size="large" /></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-guide="results-home" className="results-page">
        <header className="results-page-header">
          <div>
            <Title level={1}>{t('results.title')}</Title>
            <Paragraph>История прохождения тестов, достижения и контроль пересдач.</Paragraph>
          </div>
          <Button type="primary" icon={<FileTextOutlined />} onClick={() => navigate('/')}>
            Личный кабинет
          </Button>
        </header>

        {loadError && (
          <Alert
            className="results-load-alert"
            type="error"
            showIcon
            message={loadError}
            action={<Button size="small" icon={<ReloadOutlined />} onClick={fetchAttempts}>Повторить</Button>}
          />
        )}

        {stats && (
          <>
            <section className="results-section">
              <div className="results-section-header">
                <div>
                  <Title level={3}>{t('results.stats.title')}</Title>
                  <Text type="secondary">Учтена последняя завершённая попытка по каждому тесту</Text>
                </div>
              </div>

              <div className="results-stat-grid">
                <article className="results-stat-card primary">
                  <span className="results-stat-icon"><FileTextOutlined /></span>
                  <div><strong>{stats.total_attempts ?? 0}</strong><span>{t('results.stats.total')}</span></div>
                </article>
                <article className="results-stat-card success">
                  <span className="results-stat-icon"><CheckCircleOutlined /></span>
                  <div><strong>{stats.passed_attempts ?? 0}</strong><span>{t('results.stats.passed')}</span></div>
                </article>
                <article className="results-stat-card danger">
                  <span className="results-stat-icon"><RetweetOutlined /></span>
                  <div><strong>{stats.failed_attempts ?? 0}</strong><span>На пересдачу</span></div>
                </article>
                <article className="results-stat-card primary">
                  <span className="results-stat-icon"><BarChartOutlined /></span>
                  <div><strong>{Number(stats.average_score ?? 0).toFixed(1)}%</strong><span>{t('results.stats.average')}</span></div>
                </article>
                <article className="results-stat-card neutral">
                  <span className="results-stat-icon"><ClockCircleOutlined /></span>
                  <div><strong>{formatTime(stats.average_time_seconds ?? 0)}</strong><span>{t('results.stats.time')}</span></div>
                </article>
              </div>
            </section>

            {stats.block_ratings && stats.block_ratings.length > 0 && (
              <section className="results-section results-ratings-section">
                <div className="results-section-header">
                  <div>
                    <Title level={3}>Рейтинг по блокам</Title>
                    <Text type="secondary">Лучшие и средние результаты по направлениям</Text>
                  </div>
                </div>

                <Row gutter={[16, 16]}>
                  {stats.block_ratings.map((block: any) => {
                    const trophyClass = block.trophy_type === 'gold'
                      ? 'gold'
                      : block.trophy_type === 'silver'
                        ? 'silver'
                        : 'regular';
                    const averageScore = Number(block.average_score ?? 0);
                    const maxScore = Number(block.max_score ?? 0);

                    return (
                      <Col xs={24} sm={12} lg={8} xl={6} key={block.block}>
                        <article className={`results-rating-card ${trophyClass}`}>
                          <div className="results-rating-top">
                            <span className="results-rating-icon"><TrophyOutlined /></span>
                            <Tag>{block.attempts_count} попыток</Tag>
                          </div>
                          <Title level={4}>{getBlockName(block.block_name)}</Title>
                          <div className="results-rating-best">
                            <span>Лучший результат</span>
                            <strong className={maxScore >= 70 ? 'success' : 'danger'}>{maxScore.toFixed(1)}%</strong>
                          </div>
                          <Progress
                            percent={Math.min(100, Math.max(0, averageScore))}
                            showInfo={false}
                            strokeColor="#2563eb"
                            trailColor="#eaecf0"
                          />
                          <div className="results-rating-average">
                            <span>Средний результат</span><strong>{averageScore.toFixed(1)}%</strong>
                          </div>
                        </article>
                      </Col>
                    );
                  })}
                </Row>
              </section>
            )}
          </>
        )}

        <section className="results-section results-history-section">
          <div className="results-section-header results-history-header">
            <div>
              <Title level={3}>История попыток</Title>
              <Text type="secondary">Неудачные попытки выделены и требуют пересдачи</Text>
            </div>
            <Input
              className="results-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Найти тест"
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
            />
          </div>

          <div data-guide="results-filters" className="results-filter-row">
            <Button type={attemptFilter === 'all' ? 'primary' : 'default'} onClick={() => setAttemptFilter('all')}>
              Все попытки <span>{attempts.length}</span>
            </Button>
            <Button type={attemptFilter === 'passed' ? 'primary' : 'default'} onClick={() => setAttemptFilter('passed')}>
              Сдано <span>{attempts.filter(item => item.is_passed).length}</span>
            </Button>
            <Button
              danger={attemptFilter === 'failed'}
              type={attemptFilter === 'failed' ? 'primary' : 'default'}
              onClick={() => setAttemptFilter('failed')}
            >
              На пересдачу <span>{attempts.filter(item => !item.is_passed).length}</span>
            </Button>
          </div>

          {attempts.length === 0 ? (
            <Empty className="results-empty" description={t('results.noResults')} />
          ) : filteredAttempts.length === 0 ? (
            <Empty className="results-empty" description="По выбранным условиям попыток не найдено" />
          ) : (
            <div className="results-attempt-grid">
              {filteredAttempts.map((attempt) => {
                const isPassed = Boolean(attempt.is_passed);
                const score = Number(attempt.score ?? 0);
                const completedAt = attempt.completed_at || attempt.started_at;

                return (
                  <Card
                    key={attempt.id}
                    className={`results-attempt-card ${isPassed ? 'passed' : 'failed'}`}
                  >
                    <div className="results-attempt-top">
                      <Tag icon={isPassed ? <CheckCircleOutlined /> : <RetweetOutlined />}>
                        {isPassed ? t('results.passed') : 'Пересдача'}
                      </Tag>
                      <Text type="secondary">Попытка №{attemptNumbers.get(attempt.id)}</Text>
                    </div>

                    <Title level={4}>{attempt.test_title || 'Тест'}</Title>

                    <div className="results-attempt-score-row">
                      <Progress
                        type="circle"
                        percent={Math.min(100, Math.max(0, score))}
                        size={76}
                        strokeWidth={8}
                        strokeColor={isPassed ? '#12b76a' : '#f04438'}
                        trailColor="#eaecf0"
                        format={() => <span>{score.toFixed(1)}%</span>}
                      />
                      <div className="results-attempt-meta">
                        <span><CalendarOutlined /> {dayjs(completedAt).format('DD.MM.YYYY HH:mm')}</span>
                        <span><ClockCircleOutlined /> {formatTime(attempt.time_spent_seconds ?? 0)}</span>
                        {!isPassed && <strong><RetweetOutlined /> Требуется повторное прохождение</strong>}
                      </div>
                    </div>

                    <div className="results-attempt-actions">
                      <Button type="primary" icon={<ArrowRightOutlined />} onClick={() => openAttemptDetails(attempt.id)}>
                        {t('results.showDetails')}
                      </Button>
                      <Button icon={<DownloadOutlined />} onClick={() => downloadReport(attempt.id)}>
                        PDF
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <Modal
        className="results-detail-modal"
        open={selectedAttemptId !== null}
        title={
          <div className="results-detail-title">
            <span>{selectedAttempt?.test_title || 'Детализация попытки'}</span>
            {selectedAttempt && (
              <Tag icon={selectedAttempt.is_passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                {selectedAttempt.is_passed ? t('results.passed') : t('results.failed')}
              </Tag>
            )}
          </div>
        }
        width={1040}
        onCancel={() => setSelectedAttemptId(null)}
        footer={selectedAttempt ? [
          <Button key="close" onClick={() => setSelectedAttemptId(null)}>Закрыть</Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={() => downloadReport(selectedAttempt.id)}>
            {t('results.download')}
          </Button>,
        ] : null}
      >
        {selectedAttemptId !== null && detailLoadingId === selectedAttemptId ? (
          <div className="results-detail-loading"><Spin /></div>
        ) : selectedDetails?.detailed_answers?.length ? (
          <div className="results-answer-list">
            {selectedDetails.detailed_answers.map((detail: any, idx: number) => (
              <article
                key={detail.question_id}
                className={`results-answer-card ${detail.user_answer.is_correct ? 'correct' : 'incorrect'}`}
              >
                <div className="results-answer-heading">
                  <span className="results-answer-number">{idx + 1}</span>
                  <div>
                    <Text strong>{detail.question_text}</Text>
                    <Space size={6} wrap>
                      {detail.removed_from_test && <Tag>Убран из теста · не учитывается в результате</Tag>}
                      <Tag icon={detail.user_answer.is_correct ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                        {detail.user_answer.is_correct ? t('results.correct') : t('results.incorrect')}
                      </Tag>
                      <Tag icon={<ClockCircleOutlined />}>
                        {formatQuestionTime(detail.user_answer.time_spent_seconds)}
                      </Tag>
                    </Space>
                  </div>
                </div>

                <div className="results-answer-content">
                  <div>
                    <Text type="secondary">{t('results.yourAnswer')}</Text>
                    <div className="results-answer-values">
                      {(detail.question_type === 'single_choice' || detail.question_type === 'multiple_choice') ? (
                        detail.user_answer.selected_answer_ids?.length
                          ? detail.user_answer.selected_answer_ids.map((answerId: number) => {
                            const answer = detail.all_answers.find((item: any) => item.id === answerId);
                            return answer ? <Tag key={answerId}>{answer.text}{answer.removed_from_question ? ' (вариант убран)' : ''}</Tag> : null;
                          })
                          : <Text>—</Text>
                      ) : detail.question_type === 'matching' ? (
                        Object.entries(detail.user_answer.matching_data || {}).map(([key, value]) => (
                          <Tag key={key}>{key} → {String(value)}</Tag>
                        ))
                      ) : (
                        <Text>{detail.user_answer.answer_text || '—'}</Text>
                      )}
                    </div>
                  </div>

                  {!detail.user_answer.is_correct && (
                    <div>
                      <Text type="secondary">{t('results.correctAnswer')}</Text>
                      <div className="results-answer-values correct-values">
                        {detail.question_type === 'matching'
                          ? Object.entries(detail.correct_answers?.matching || {}).map(([key, value]) => (
                            <Tag key={key}>{key} → {String(value)}</Tag>
                          ))
                          : (detail.correct_answers?.answer_texts || []).map((answerText: string, answerIndex: number) => (
                            <Tag key={`${answerText}-${answerIndex}`}>{answerText}</Tag>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <Empty description="Детализация для этой попытки недоступна" />
        )}
      </Modal>
    </Layout>
  );
};

export default Results;
